import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import useToast from '../hooks/useToast';
import { authenticatedFetch } from '../api/apiClient';
import * as backendApi from '../api/backendApi';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { io } from 'socket.io-client';
import MainLayout from '../components/layout/MainLayout';
import { LocalAgentStatusCard } from '../components/LocalAgentStatusCard';

/**
 * Test Tools Page
 * Provides utilities to test Twilio and SendGrid integrations
 */
const TestToolsPage = () => {
  useEffect(() => {
    console.log('[TestToolsPage] Mounted');
    const scriptErrorHandler = e => {
      if (e.target.tagName === 'SCRIPT' && e.target.src) {
        console.error('[TestToolsPage] Script load error:', e.target.src);
        if (window.fetch) {
          fetch(e.target.src).then(resp => {
            if (resp.headers.get('content-type') === 'text/html') {
              console.error(
                '[TestToolsPage] Received HTML (likely index.html) instead of JS for:',
                e.target.src
              );
            }
          });
        }
      }
    };
    window.addEventListener('error', scriptErrorHandler, true);

    // Dynamic import test
    import('../api/apiClient.js')
      .then(mod => {
        console.log('[TestToolsPage] apiClient.js loaded:', mod);
      })
      .catch(err => {
        console.error('[TestToolsPage] Failed to dynamically import apiClient.js:', err);
      });

    return () => {
      window.removeEventListener('error', scriptErrorHandler, true);
      console.log('[TestToolsPage] Unmounted');
    };
  }, []);
  // Use the shared toast utility (not a true React hook)
  // This is initialized outside of any conditional logic to follow React rules
  const toast = useToast();

  // Custom confirm dialog using native window.confirm to avoid any hook issues
  const confirmAction = (message, onConfirm) => {
    if (window.confirm(message)) {
      onConfirm();
    }
  };

  // Form state
  // Email form state
  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: '',
    message: '',
  });

  // Cloud Run Email form state
  const [cloudEmailForm, setCloudEmailForm] = useState({
    to: '',
    subject: '',
    body: '',
  });

  // Cloud Run SIP Call form state
  const [cloudSipForm, setCloudSipForm] = useState({
    to: '',
    message: '',
  });

  // SMS form state
  const [smsForm, setSmsForm] = useState({
    to: '',
    message: '',
  });

  // SIP call form state
  const [sipForm, setSipForm] = useState({
    target: '',
    message: '',
  });

  // Response data state
  const [emailResponse, setEmailResponse] = useState(null);
  const [cloudEmailResponse, setCloudEmailResponse] = useState(null);
  const [cloudSipResponse, setCloudSipResponse] = useState(null);
  const [smsResponse, setSmsResponse] = useState(null);
  const [sipResponse, setSipResponse] = useState(null);

  // Loading states
  const [emailLoading, setEmailLoading] = useState(false);
  const [cloudEmailLoading, setCloudEmailLoading] = useState(false);
  const [cloudSipLoading, setCloudSipLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [sipLoading, setSipLoading] = useState(false);
  const [sipDebugLoading, setSipDebugLoading] = useState(false);

  // State for SIP debug panel
  const [sipDebugData, setSipDebugData] = useState(null);

  // State for SMS connection verification
  const [smsConnectionStatus, setSmsConnectionStatus] = useState(null);

  // Real-time SIP call monitoring
  const [realtimeSipCalls, setRealtimeSipCalls] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  // Handle email form input changes
  const handleEmailChange = e => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle Cloud Run email form input changes
  const handleCloudEmailChange = e => {
    const { name, value } = e.target;
    setCloudEmailForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle Cloud Run SIP call form input changes
  const handleCloudSipChange = e => {
    const { name, value } = e.target;
    setCloudSipForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle SMS form input changes
  const handleSmsChange = e => {
    const { name, value } = e.target;
    setSmsForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle SIP form input changes
  const handleSipChange = e => {
    const { name, value } = e.target;
    setSipForm(prev => ({ ...prev, [name]: value }));
  };

  // Send test email
  const sendTestEmail = async () => {
    // Validate form
    if (!emailForm.to || !emailForm.subject || !emailForm.message) {
      toast.error('Please fill in all email fields');
      return;
    }

    // Confirm before sending
    confirmAction(`Send test email to ${emailForm.to}?`, handleSendEmail);
  };

  // Send test email via Cloud Run backend
  const sendCloudEmail = async () => {
    // Validate form
    if (!cloudEmailForm.to || !cloudEmailForm.subject || !cloudEmailForm.body) {
      toast.error('Please fill in all email fields');
      return;
    }

    // Confirm before sending
    confirmAction(`Send test email to ${cloudEmailForm.to} via Cloud Run?`, handleSendCloudEmail);
  };

  // Send test SIP call via Cloud Run backend
  const sendCloudSipCall = async () => {
    // Validate form
    if (!cloudSipForm.to || !cloudSipForm.message) {
      toast.error('Please fill in all SIP call fields');
      return;
    }

    // Confirm before sending
    confirmAction(`Place SIP call to ${cloudSipForm.to} via Cloud Run?`, handleSendCloudSipCall);
  };

  // Handle actual email sending
  const handleSendEmail = async () => {
    try {
      setEmailLoading(true);
      setEmailResponse(null);

      // Check if the backend is available first
      try {
        const response = await backendApi.callAtURL(API_ROUTES.TEST_SEND_EMAIL, 'POST', emailForm);

        setEmailResponse(response);
        toast.success('Email sent successfully');
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to backend server. Please ensure the backend is running and CORS is properly configured.'
          );
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(`Failed to send email: ${error.message}`);
      setEmailResponse({
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the backend server not running.',
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle Cloud Run email sending
  const handleSendCloudEmail = async () => {
    try {
      setCloudEmailLoading(true);
      setCloudEmailResponse(null);

      try {
        // Use authenticatedFetch to Cloud Run endpoint
        const response = await authenticatedFetch(API_ROUTES.TEST_SEND_EMAIL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: cloudEmailForm.to,
            subject: cloudEmailForm.subject,
            message: cloudEmailForm.body,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server responded with ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        setCloudEmailResponse(data);
        toast.success('Email sent successfully via Cloud Run');
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to Cloud Run backend. Please check your network connection and CORS configuration.'
          );
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error sending email via Cloud Run:', error);
      toast.error(`Failed to send email: ${error.message}`);
      setCloudEmailResponse({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the Cloud Run backend being unavailable.',
      });
    } finally {
      setCloudEmailLoading(false);
    }
  };

  // Send test SMS
  const sendTestSms = async () => {
    // Validate form
    if (!smsForm.to || !smsForm.message) {
      toast.error('Please fill in all SMS fields');
      return;
    }

    // Confirm before sending
    confirmAction(`Send test SMS to ${smsForm.to}?`, handleSendSms);
  };

  // Handle actual SMS sending
  const handleSendSms = async () => {
    try {
      setSmsLoading(true);
      setSmsResponse(null);

      try {
        // Use authenticatedFetch to Cloud Run endpoint
        const response = await authenticatedFetch(API_ROUTES.TEST_SEND_SMS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: smsForm.to,
            message: smsForm.message,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server responded with ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        setSmsResponse(data);
        toast.success('SMS sent successfully via Cloud Run');
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to Cloud Run backend. Please check your network connection and CORS configuration.'
          );
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error sending SMS via Cloud Run:', error);
      toast.error(`Failed to send SMS: ${error.message}`);
      setSmsResponse({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the Cloud Run backend being unavailable.',
      });
    } finally {
      setSmsLoading(false);
    }
  };

  // Check SMS connection and endpoint availability
  const checkSmsConnection = async () => {
    try {
      toast.info('Checking SMS endpoint availability...');

      const response = await authenticatedFetch(API_ROUTES.TEST_DEBUG_SMS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      setSmsConnectionStatus(data);

      if (data.sms_route_registered) {
        toast.success('SMS endpoint is properly registered in the backend!');
      } else {
        toast.error('SMS endpoint is not properly registered. Please check backend configuration.');
      }

      // Display more detailed information in the console for debugging
      console.log('SMS Debug Information:', data);
    } catch (error) {
      console.error('Error checking SMS connection:', error);
      toast.error(`Failed to check SMS connection: ${error.message}`);
      setSmsConnectionStatus({
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'Error checking SMS connection. The debug endpoint may not be available.',
      });
    }
  };

  // Handle Cloud Run SIP call sending
  const handleSendCloudSipCall = async () => {
    try {
      setCloudSipLoading(true);
      setCloudSipResponse(null);

      try {
        // Use authenticatedFetch to Cloud Run endpoint
        const response = await authenticatedFetch(API_ROUTES.TEST_SIP_CALL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: cloudSipForm.to,
            message: cloudSipForm.message,
            messageType: 'tts',
            ttsLanguage: 'en-US',
            fileUrl: null,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server responded with ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        setCloudSipResponse(data);
        toast.success('SIP call placed successfully via Cloud Run');
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to Cloud Run backend. Please check your network connection and CORS configuration.'
          );
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error placing SIP call via Cloud Run:', error);
      toast.error(`Failed to place SIP call: ${error.message}`);
      setCloudSipResponse({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the Cloud Run backend being unavailable.',
      });
    } finally {
      setCloudSipLoading(false);
    }
  };

  // Send test SIP call
  const sendTestSipCall = async () => {
    // Validate form
    if (!sipForm.target || !sipForm.message) {
      toast.error('Please fill in all SIP call fields');
      return;
    }

    // Confirm before sending
    confirmAction(`Place SIP call to ${sipForm.target}?`, handleSendSipCall);
  };

  // Handle actual SIP call
  const handleSendSipCall = async () => {
    try {
      setSipLoading(true);
      setSipResponse(null);

      try {
        const response = await backendApi.callAtURL(API_ROUTES.TEST_SEND_SIP_CALL, 'POST', {
          target: sipForm.target,
          message: sipForm.message,
        });

        setSipResponse(response);
        toast.success('SIP call placed successfully');
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to backend server. Please ensure the backend is running and CORS is properly configured.'
          );
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error placing SIP call:', error);
      toast.error(`Failed to place SIP call: ${error.message}`);
      setSipResponse({
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the backend server not running.',
      });
    } finally {
      setSipLoading(false);
    }
  };

  // Fetch last incoming SIP call for debugging
  const fetchLastIncomingSip = async () => {
    try {
      setSipDebugLoading(true);
      setSipDebugData(null);

      try {
        const response = await backendApi.callAtURL(API_ROUTES.TEST_LAST_INCOMING_SIP, 'GET');

        if (response && response.success && response.call) {
          setSipDebugData(response.call);
          toast.success('Retrieved last SIP call data');
        } else {
          toast.error('No data found');
        }
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          // CORS or connectivity issue
          throw new Error(
            'Cannot connect to backend server. Please ensure the backend is running and CORS is properly configured.'
          );
        } else if (error.message && error.message.includes('404')) {
          toast.error('No recent SIP call found');
          return;
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error fetching SIP call data:', error);
      toast.error(`Failed to fetch SIP call data: ${error.message}`);
      setSipDebugData({
        error: error.message,
        timestamp: new Date().toISOString(),
        details: 'This may be due to CORS issues or the backend server not running.',
      });
    } finally {
      setSipDebugLoading(false);
    }
  };

  // Function to clear real-time SIP call data
  const clearRealtimeSipCalls = () => {
    setRealtimeSipCalls([]);
    toast.success('SIP call history cleared');
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Clean up any pending toasts on unmount
      toast.dismiss();
    };
  }, []); // No dependencies needed since toast is not a hook result

  // --- WebSocket connection for real-time SIP call monitoring is temporarily disabled due to socket errors ---
  /*
  useEffect(() => {
    let socket = null;
    
    const connectWebSocket = () => {
      try {
        // Get WebSocket server URL from environment or use relative WebSocket path
        const socketUrl = import.meta.env.VITE_WEBSOCKET_URL || '';
        
        // Create socket connection
        socket = io(socketUrl, {
          transports: ['websocket'],
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
  */
  //           reconnectionDelay: 1000
  //         });
  //
  //         socketRef.current = socket;
  //
  //         // Socket event handlers
  //         socket.on('connect', () => {
  //           setIsConnected(true);
  //           toast.success('WebSocket connected');
  //           console.log('WebSocket connected');
  //         });
  //
  //         socket.on('disconnect', () => {
  //           setIsConnected(false);
  //           console.log('WebSocket disconnected');
  //           // Don't show error toast on normal disconnection
  //         });
  //
  //         socket.on('connect_error', (error) => {
  //           console.error('WebSocket connection error:', error);
  //           // Only show one error toast to avoid spamming
  //           if (isConnected) {
  //             toast.error(`WebSocket connection error: ${error.message}`);
  //             setIsConnected(false);
  //           }
  //         });
  //
  //         // Listen for SIP incoming call events
  //         socket.on('sip-incoming', (data) => {
  //           console.log('SIP incoming call event received:', data);
  //
  //           // Add timestamp to the data
  //           const callData = {
  //             ...data,
  //             receivedAt: new Date().toISOString()
  //           };
  //
  //           // Add to the beginning of the array (newest first)
  //           setRealtimeSipCalls(prev => {
  //             const newCalls = [callData, ...prev];
  //             // Keep only the last 3 calls
  //             return newCalls.slice(0, 3);
  //           });
  //
  //           // Show toast notification
  //           const caller = data.callerId || data.from || 'Unknown caller';
  //           toast.success(`📞 SIP call received from ${caller}`);
  //         });
  //       } catch (error) {
  //         console.error('Error initializing WebSocket:', error);
  //         toast.error(`WebSocket initialization error: ${error.message}`);
  //       }
  //     };
  //
  //     // Initialize connection
  //     connectWebSocket();
  //
  //     // Cleanup on unmount
  //     return () => {
  //       if (socket) {
  //         socket.disconnect();
  //         console.log('WebSocket disconnected on cleanup');
  //       }
  //     };
  //   }, [isConnected]); // Remove toast from dependencies since it's not a hook result

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <Toaster position="top-right" />
        <h1 className="text-2xl font-bold mb-6">Communication Test Tools V708</h1>
        <p className="text-gray-600 mb-8">Test email, SMS, and SIP call functionality</p>

        {/* Local Windows Agent status + download */}
        <div style={{ marginBottom: '1.5rem' }}>
          <LocalAgentStatusCard />
        </div>

        {/* Backend Status Alert */}
        {/* <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Note:</strong> This page requires a running backend server with proper CORS configuration. If you're seeing connection errors, please ensure the backend server is running and has CORS enabled.
            </p>
          </div>
        </div>
      </div> */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Cloud Run Email Test Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-purple-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Cloud Run Email Test
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Email</label>
                <input
                  type="email"
                  name="to"
                  value={cloudEmailForm.to}
                  onChange={handleCloudEmailChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="recipient@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={cloudEmailForm.subject}
                  onChange={handleCloudEmailChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Test Email Subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                <textarea
                  name="body"
                  value={cloudEmailForm.body}
                  onChange={handleCloudEmailChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Your email message here..."
                ></textarea>
              </div>
              <button
                onClick={sendCloudEmail}
                disabled={cloudEmailLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
              >
                {cloudEmailLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Email via Cloud Run'
                )}
              </button>
            </div>

            {/* Cloud Email Response Display */}
            {cloudEmailResponse && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(cloudEmailResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Cloud Run SIP Call Test Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-purple-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Cloud Run SIP Call Test
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIP Number</label>
                <input
                  type="text"
                  name="to"
                  value={cloudSipForm.to}
                  onChange={handleCloudSipChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="sip:user@domain.com or phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  name="message"
                  value={cloudSipForm.message}
                  onChange={handleCloudSipChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Message to be spoken during the call..."
                ></textarea>
              </div>
              <button
                onClick={sendCloudSipCall}
                disabled={cloudSipLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
              >
                {cloudSipLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Calling...
                  </>
                ) : (
                  'Send SIP Call via Cloud Run'
                )}
              </button>
            </div>

            {/* Cloud SIP Call Response Display */}
            {cloudSipResponse && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(cloudSipResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Email Test Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Send Test Email
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Email</label>
                <input
                  type="email"
                  name="to"
                  value={emailForm.to}
                  onChange={handleEmailChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="recipient@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={emailForm.subject}
                  onChange={handleEmailChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Test Email Subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  name="message"
                  value={emailForm.message}
                  onChange={handleEmailChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Your email message here..."
                ></textarea>
              </div>
              <button
                onClick={sendTestEmail}
                disabled={emailLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
              >
                {emailLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
            </div>

            {/* Email Response Display */}
            {emailResponse && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(emailResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* SMS Test Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* SMS API Endpoint Warning */}
            <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    SMS API endpoint issue (404)
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      The SMS functionality is currently experiencing a 404 error. This is likely
                      due to one of the following issues:
                    </p>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>
                        The <code>test_routes</code> blueprint might not be properly registered in{' '}
                        <code>main.py</code>
                      </li>
                      <li>
                        The <code>/api/test/send-sms</code> endpoint might be missing in the backend
                      </li>
                      <li>
                        Verify that the SMS-related handler is correctly imported and configured
                      </li>
                    </ul>
                    <button
                      onClick={checkSmsConnection}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Verify SMS Connection
                    </button>

                    {smsConnectionStatus && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700">Diagnostic Results:</h4>
                        <div className="mt-1 text-xs">
                          <div className="flex items-center">
                            <span className="font-medium mr-2">SMS Route Registered:</span>
                            {smsConnectionStatus.sms_route_registered ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-red-600">✗ No</span>
                            )}
                          </div>
                          {smsConnectionStatus.test_routes_registered !== undefined && (
                            <div className="flex items-center mt-1">
                              <span className="font-medium mr-2">Test Routes Blueprint:</span>
                              {smsConnectionStatus.test_routes_registered ? (
                                <span className="text-green-600">✓ Registered</span>
                              ) : (
                                <span className="text-red-600">✗ Not Registered</span>
                              )}
                            </div>
                          )}
                          {smsConnectionStatus.error && (
                            <div className="mt-1 text-red-600">
                              Error: {smsConnectionStatus.error}
                            </div>
                          )}
                          <div className="mt-2 text-gray-600 italic">
                            {smsConnectionStatus.message ||
                              'Check the developer console for more detailed diagnostic information'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              Cloud Run SMS Test
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Phone Number
                </label>
                <input
                  type="tel"
                  name="to"
                  value={smsForm.to}
                  onChange={handleSmsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  name="message"
                  value={smsForm.message}
                  onChange={handleSmsChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Your SMS message here..."
                ></textarea>
              </div>
              <button
                onClick={sendTestSms}
                disabled={smsLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
              >
                {smsLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send SMS via Cloud Run'
                )}
              </button>
            </div>

            {/* SMS Response Display */}
            {smsResponse && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(smsResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* SIP Call Test Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 text-purple-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Place SIP Call
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIP Address</label>
                <input
                  type="text"
                  name="target"
                  value={sipForm.target}
                  onChange={handleSipChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="sip:user@domain.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message to Speak
                </label>
                <textarea
                  name="message"
                  value={sipForm.message}
                  onChange={handleSipChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Message to be spoken during the call..."
                ></textarea>
              </div>
              <button
                onClick={sendTestSipCall}
                disabled={sipLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
              >
                {sipLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Calling...
                  </>
                ) : (
                  'Call Now'
                )}
              </button>
            </div>

            {/* SIP Call Response Display */}
            {sipResponse && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Response:</p>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(sipResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* SIP Call Debug Panel */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-2 text-indigo-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            SIP Call Debug Info
          </h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              View the latest incoming SIP call webhook payload received from Twilio. This is useful
              for debugging inbound SIP call handling and understanding the data structure.
            </p>

            <button
              onClick={fetchLastIncomingSip}
              disabled={sipDebugLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
            >
              {sipDebugLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Fetching...
                </>
              ) : (
                'Fetch Last SIP Call'
              )}
            </button>

            <div className="mt-4 border border-gray-200 rounded-md p-4">
              <h3 className="text-md font-medium text-gray-700 mb-2">Webhook Payload:</h3>
              {sipDebugData ? (
                <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                  {JSON.stringify(sipDebugData, null, 2)}
                </pre>
              ) : (
                <div className="text-gray-500 italic py-4 text-center">
                  No data yet. Click the button above to fetch the latest SIP call data.
                </div>
              )}
            </div>

            {sipDebugData && (
              <div className="mt-4">
                <h3 className="text-md font-medium text-gray-700 mb-2">Key Fields:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {['From', 'To', 'CallSid', 'CallerName', 'Direction', 'SipDomainSid'].map(
                    field =>
                      sipDebugData[field.toLowerCase()] && (
                        <div key={field} className="flex">
                          <span className="font-medium text-gray-600 mr-2">{field}:</span>
                          <span className="text-gray-800">{sipDebugData[field.toLowerCase()]}</span>
                        </div>
                      )
                  )}
                  <div className="flex">
                    <span className="font-medium text-gray-600 mr-2">Timestamp:</span>
                    <span className="text-gray-800">
                      {sipDebugData.timestamp
                        ? new Date(sipDebugData.timestamp).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Real-time SIP Call Monitoring Panel */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-2 text-purple-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            SIP Incoming Call Debug
            <span
              className={`ml-3 px-2 py-1 text-xs rounded-full ${isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Real-time monitoring of SIP call webhook events from Twilio. This panel automatically
              displays incoming SIP calls as they are received by the backend.
            </p>

            <div className="flex justify-end">
              <button
                onClick={clearRealtimeSipCalls}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md flex items-center text-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Clear
              </button>
            </div>

            {realtimeSipCalls.length > 0 ? (
              <div className="space-y-4">
                {realtimeSipCalls.map((call, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-md font-medium text-gray-800">
                        Call from: {call.callerId || call.from || 'Unknown'}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {new Date(call.receivedAt).toLocaleTimeString()}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      {['from', 'to', 'callSid', 'callerName', 'direction', 'sipDomainSid'].map(
                        field =>
                          call[field] && (
                            <div key={field} className="flex">
                              <span className="font-medium text-gray-600 mr-2">
                                {field.charAt(0).toUpperCase() + field.slice(1)}:
                              </span>
                              <span className="text-gray-800">{call[field]}</span>
                            </div>
                          )
                      )}
                    </div>

                    <div className="mt-2">
                      <button
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        onClick={() => {
                          const el = document.getElementById(`call-details-${index}`);
                          if (el) el.classList.toggle('hidden');
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Toggle Full Details
                      </button>
                      <pre
                        id={`call-details-${index}`}
                        className="hidden mt-2 bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40"
                      >
                        {JSON.stringify(call, null, 2)}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md p-8 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 mx-auto text-gray-400 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <p className="text-gray-500">No SIP calls received yet</p>
                <p className="text-gray-400 text-sm mt-1">
                  Incoming calls will appear here automatically
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TestToolsPage;
